import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import axios from 'axios';
import * as XLSX from 'xlsx';
import DetailModal from '../components/DetailModal';
import './FlightBookings.css';

interface Traveler {
  first_name: string;
  last_name: string;
  email: string;
  ticketId: string | null;
  contact_no?: string;
}

interface Ticket {
  ticket_id: number;
  pnr: string;
  status: string;
  fare: string;
  issued_at: string;
  booking_id?: number;
}

interface FlightBooking {
  booking_id: number;
  created_at: string;
  currency: string;
  email: string;
  first_name: string;
  last_name: string;
  pnr: string;
  status: string;
  tbo_booking_id: string;
  total_price: string;
  travelers: Traveler[];
  tickets?: Ticket[];
  user_id: number;
}

const FlightBookings: React.FC = () => {
  const [bookings, setBookings] = useState<FlightBooking[]>([]);
  const [filteredBookings, setFilteredBookings] = useState<FlightBooking[]>([]);
  const [invoiceMap, setInvoiceMap] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState<any | null>(null);
  const [modalBooking, setModalBooking] = useState<FlightBooking | null>(null);

  useEffect(() => {
    loadBookings();
  }, []);

  useEffect(() => {
    filterAndSort();
  }, [bookings, searchTerm, statusFilter, sortBy]);

  const loadBookings = async () => {
    setLoading(true);
    try {
      const res = await api.getFlightOrders(1, 100);
      const orders = res.flight_orders || [];
      setBookings(orders);
      setFilteredBookings(orders);

      // Load invoices map
      try {
        const invResp = await api.getInvoicesFromServer(1, 500);
        const map: Record<string, any> = {};
        if (invResp?.invoices) {
          invResp.invoices.forEach((inv: any) => {
            const type = inv.type || 'flight';
            const bid = inv.booking_id ?? inv.bookingId ?? inv.id ?? inv.order_id;
            if (type && bid) map[`${type}-${bid}`] = inv;
          });
        }
        setInvoiceMap(map);
      } catch (e) {
        console.warn('Invoice load failed', e);
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load flight bookings');
    } finally {
      setLoading(false);
    }
  };

  const filterAndSort = () => {
    let filtered = [...bookings];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(b =>
        b.pnr?.toLowerCase().includes(term) ||
        b.first_name?.toLowerCase().includes(term) ||
        b.last_name?.toLowerCase().includes(term) ||
        b.email?.toLowerCase().includes(term) ||
        b.tbo_booking_id?.toLowerCase().includes(term) ||
        b.booking_id?.toString().includes(term)
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(b => b.status?.toLowerCase() === statusFilter.toLowerCase());
    }

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'created':
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        case 'amount':
          return (Number(b.total_price) || 0) - (Number(a.total_price) || 0);
        case 'pnr':
          return (a.pnr || '').localeCompare(b.pnr || '');
        default:
          return 0;
      }
    });

    setFilteredBookings(filtered);
  };

  const handleView = async (booking: FlightBooking) => {
    try {
      const id = booking.booking_id;
      const res = await api.getFlightOrderDetail(id);
      setModalData(res?.flight_order || res);
      setModalBooking(booking);
      setModalOpen(true);
    } catch (err) {
      alert('Failed to fetch details');
    }
  };

  const downloadBlob = (resp: any, filename: string) => {
    const blob = new Blob([resp.data], { type: resp.headers['content-type'] || 'application/pdf' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const handleDownloadInvoice = async (booking: FlightBooking) => {
    try {
      const id = booking.booking_id;
      const { url, headers } = await api.downloadInvoice('flight', id);
      const resp = await axios.get(url, { headers, responseType: 'blob' });
      downloadBlob(resp, `invoice_flight_${id}.pdf`);
    } catch (err) {
      alert('Invoice download failed');
    }
  };

  // UPDATED: use PNR for ticket download
  const handleDownloadTicket = async (pnr: string) => {
    if (!pnr) {
      alert('PNR not available');
      return;
    }
    try {
      const { url, headers } = await api.downloadTicket('flight', pnr);
      const resp = await axios.get(url, { headers, responseType: 'blob' });
      downloadBlob(resp, `ticket_${pnr}.pdf`);
    } catch (err: any) {
      if (err?.response?.status === 404) alert('Ticket not found');
      else alert('Ticket download failed');
    }
  };

  const getStatusClass = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'ticketed':
      case 'confirmed':
        return 'badge-success';
      case 'pending':
        return 'badge-warning';
      case 'cancelled':
        return 'badge-danger';
      default:
        return 'badge-secondary';
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };
 // Inside FlightBookings component, add this function
const handleTallyExport = async () => {
  try {
    const params = new URLSearchParams();
    if (searchTerm) params.append('search', searchTerm);
    if (statusFilter !== 'all') params.append('status', statusFilter);
    // Optional: add date range if you add date pickers later

    const response = await api.exportFlightTally(params);
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'flight_bookings_tally.xml');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (err: any) {
    if (err.response?.status === 404) {
      alert('No bookings to export');
    } else {
      console.error('Tally export error', err);
      alert('Export failed. Please try again.');
    }
  }
};

  const handleExcelExport = () => {
    const rows = filteredBookings.map((b) => ({
      'Booking ID': b.booking_id,
      'PNR': b.pnr || '',
      'TBO Booking ID': b.tbo_booking_id || '',
      'Status': b.status || '',
      'First Name': b.first_name || '',
      'Last Name': b.last_name || '',
      'Email': b.email || '',
      'Passengers': b.travelers?.length ?? 0,
      'Total Price': Number(b.total_price) || 0,
      'Currency': b.currency || 'INR',
      'Created At': b.created_at ? new Date(b.created_at).toLocaleString() : '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Flight Bookings');
    XLSX.writeFile(wb, 'flight_bookings.xlsx');
  };

  return (
    <div className="flight-bookings-page">
      <div className="page-header">
  <div>
    <h1>Flight Bookings</h1>
    <p>Manage all flight reservations</p>
  </div>
  <div style={{ display: 'flex', gap: '12px' }}>
    <button className="btn-primary" onClick={handleTallyExport}>
      📥 Download for Tally
    </button>
    <button className="btn-primary" onClick={handleExcelExport}>
      📊 Download Excel
    </button>
  </div>
</div>
      
      <div className="filters-bar">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search by PNR, name, email, booking ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All Status</option>
            <option value="ticketed">Ticketed</option>
            <option value="pending">Pending</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="created">Newest</option>
            <option value="amount">Amount</option>
            <option value="pnr">PNR</option>
          </select>
        </div>
      </div>

      <div className="stats-mini">
        <div className="stat-mini-item">
          <span className="stat-mini-label">Total</span>
          <span className="stat-mini-value">{filteredBookings.length}</span>
        </div>
        <div className="stat-mini-item">
          <span className="stat-mini-label">Revenue</span>
          <span className="stat-mini-value">
            ₹{filteredBookings.reduce((sum, b) => sum + (Number(b.total_price) || 0), 0).toLocaleString()}
          </span>
        </div>
        <div className="stat-mini-item">
          <span className="stat-mini-label">Ticketed</span>
          <span className="stat-mini-value">
            {filteredBookings.filter(b => b.status?.toLowerCase() === 'ticketed').length}
          </span>
        </div>
      </div>

      {loading && (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading flight bookings...</p>
        </div>
      )}

      {error && (
        <div className="error-state">
          <span className="error-icon">⚠️</span>
          <p>{error}</p>
          <button onClick={loadBookings} className="btn-outline">Retry</button>
        </div>
      )}

      {!loading && !error && (
        <div className="bookings-grid">
          {filteredBookings.map((booking) => {
            const invoiceKey = `flight-${booking.booking_id}`;
            const hasInvoice = !!invoiceMap[invoiceKey];
            const passengerCount = booking.travelers.length;

            return (
              <div key={booking.booking_id} className="booking-card">
                <div className="card-header">
                  <div className="booking-type">
                    <span className="type-icon">✈️</span>
                    <span className="type-label">FLIGHT</span>
                    <span className="booking-id-label">#{booking.booking_id}</span>
                  </div>
                  <span className={`status-badge ${getStatusClass(booking.status)}`}>
                    {booking.status || 'N/A'}
                  </span>
                </div>

                <div className="card-body">
                  <div className="pnr-section">
                    <div className="pnr-box">
                      <span className="pnr-label">PNR</span>
                      <span className="pnr-value">{booking.pnr}</span>
                    </div>
                    <div className="tbo-id">TBO ID: {booking.tbo_booking_id}</div>
                  </div>

                  <div className="primary-guest">
                    <div className="user-avatar">
                      {booking.first_name?.charAt(0)}{booking.last_name?.charAt(0)}
                    </div>
                    <div className="guest-info">
                      <div className="guest-name">{booking.first_name} {booking.last_name}</div>
                      <div className="guest-email">{booking.email}</div>
                    </div>
                  </div>

                  <div className="passengers-section">
                    <div className="passengers-title">
                      {passengerCount} Passenger{passengerCount > 1 ? 's' : ''}
                    </div>
                    <div className="passenger-list">
                      {booking.travelers.map((t, idx) => (
                        <div key={idx} className="passenger-item">
                          <span className="passenger-name">{t.first_name} {t.last_name}</span>
                          {t.ticketId ? (
                            <button
                              className="ticket-icon"
                              onClick={() => handleDownloadTicket(booking.pnr)}
                              title={`Download ticket for ${t.first_name}`}
                            >
                              🎫
                            </button>
                          ) : (
                            <span className="no-ticket" title="Ticket not generated">⏳</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="booking-meta">
                    <div className="meta-date">
                      <span className="meta-icon">📅</span>
                      <span>{new Date(booking.created_at).toLocaleDateString()}</span>
                    </div>
                    <div className="meta-amount">
                      ₹{Number(booking.total_price).toLocaleString()}
                    </div>
                  </div>
                </div>

                <div className="card-footer">
                  <button className="btn-view" onClick={() => handleView(booking)}>View</button>
                  <div className="action-buttons">
                    {hasInvoice && (
                      <button className="btn-icon" onClick={() => handleDownloadInvoice(booking)} title="Download Invoice">
                        📄
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {filteredBookings.length === 0 && (
            <div className="empty-state">
              <span className="empty-icon">📭</span>
              <h3>No flight bookings found</h3>
              <p>Try adjusting your filters</p>
            </div>
          )}
        </div>
      )}

      {/* Enhanced Detail Modal */}
      <DetailModal open={modalOpen} onClose={() => setModalOpen(false)} title="Flight Booking Details">
        {modalData && (
          <div className="flight-detail-modal">
            {/* Action Buttons */}
            <div className="modal-actions">
              {modalBooking && invoiceMap[`flight-${modalBooking.booking_id}`] && (
                <button className="btn-primary" onClick={() => handleDownloadInvoice(modalBooking)}>
                  Download Invoice
                </button>
              )}
            </div>

            {/* Booking Information */}
            <div className="detail-section">
              <h3 className="section-title">Booking Information</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Booking ID</span>
                  <span className="info-value">#{modalData.booking_id}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">PNR</span>
                  <span className="info-value pnr-highlight">{modalData.pnr}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">TBO Booking ID</span>
                  <span className="info-value">{modalData.tbo_booking_id}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Status</span>
                  <span className={`status-badge ${getStatusClass(modalData.status)}`}>{modalData.status}</span>
                </div>
                {/* Additional Invoice Download Button in Booking Info */}
                {modalBooking && invoiceMap[`flight-${modalBooking.booking_id}`] && (
                  <div className="info-item">
                    <span className="info-label">Invoice</span>
                    <button
                      className="btn-icon-small"
                      onClick={() => handleDownloadInvoice(modalBooking)}
                      title="Download Invoice"
                    >
                      📄 Download Invoice
                    </button>
                  </div>
                )}
                <div className="info-item">
                  <span className="info-label">Total Price</span>
                  <span className="info-value price">₹{Number(modalData.total_price).toLocaleString()} {modalData.currency}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Created At</span>
                  <span className="info-value">{formatDate(modalData.created_at)}</span>
                </div>
                <div className="info-item full-width">
                  <span className="info-label">User</span>
                  <span className="info-value">{modalData.first_name} {modalData.last_name} ({modalData.email})</span>
                </div>
              </div>
            </div>

            {/* Passengers */}
            {modalData.travelers && modalData.travelers.length > 0 && (
              <div className="detail-section">
                <h3 className="section-title">Passengers</h3>
                <div className="passengers-grid">
                  {modalData.travelers.map((t: any, idx: number) => (
                    <div key={idx} className="passenger-card">
                      <div className="passenger-avatar">
                        {t.first_name?.charAt(0)}{t.last_name?.charAt(0)}
                      </div>
                      <div className="passenger-details">
                        <div className="passenger-name">{t.first_name} {t.last_name}</div>
                        <div className="passenger-email">{t.email}</div>
                        {t.contact_no && <div className="passenger-contact">📞 {t.contact_no}</div>}
                        {t.ticketId ? (
                          <div className="ticket-info">
                            <span className="ticket-id">Ticket ID: {t.ticketId}</span>
                            <button
                              className="btn-icon-small"
                              onClick={() => handleDownloadTicket(modalData.pnr)}
                              title="Download Ticket"
                            >
                              🎫 Download Ticket
                            </button>
                          </div>
                        ) : (
                          <span className="no-ticket-badge">No Ticket</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tickets */}
            {modalData.tickets && modalData.tickets.length > 0 && (
              <div className="detail-section">
                <h3 className="section-title">Tickets</h3>
                <table className="tickets-table">
                  <thead>
                    <tr>
                      <th>Ticket ID</th>
                      <th>PNR</th>
                      <th>Status</th>
                      <th>Fare</th>
                      <th>Issued At</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modalData.tickets.map((t: any) => (
                      <tr key={t.ticket_id}>
                        <td><span className="ticket-id-badge">#{t.ticket_id}</span></td>
                        <td>{t.pnr}</td>
                        <td><span className={`status-badge-small ${getStatusClass(t.status)}`}>{t.status}</span></td>
                        <td>₹{Number(t.fare).toLocaleString()}</td>
                        <td>{formatDate(t.issued_at)}</td>
                        <td>
                          <div className="action-cell">
                            <button
                              className="btn-icon-small"
                              onClick={() => handleDownloadTicket(t.pnr)}
                              title="Download Ticket"
                            >
                              🎫
                            </button>
                            {modalBooking && invoiceMap[`flight-${modalBooking.booking_id}`] && (
                              <button
                                className="btn-icon-small"
                                onClick={() => handleDownloadInvoice(modalBooking)}
                                title="Download Invoice"
                              >
                                📄
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        {!modalData && <div className="no-data">No details available</div>}
      </DetailModal>
    </div>
  );
};

export default FlightBookings;